import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import QIcn from './queueIcons';
import FocusSession, { hasActiveFocusSession } from './FocusSession';

const TYPE_ICON = {
    translate: QIcn.translate,
    review: QIcn.review,
    approve: QIcn.approve,
    comment: QIcn.comment,
    glossary: QIcn.glossary,
};

const TYPE_CLS = {
    translate: '',
    review: '',
    approve: 'green',
    comment: 'indigo',
    glossary: 'yellow',
};

const GROUP_ORDER = ['Overdue', 'Due today', 'This week', 'Later'];

const FILTERS = [
    { id: 'All', label: 'All' },
    { id: 'translate', label: 'To translate' },
    { id: 'review', label: 'To review' },
    { id: 'approve', label: 'To approve' },
    { id: 'comment', label: 'Comments' },
    { id: 'glossary', label: 'Glossary' },
];

const Stats = ({ counts, byProject }) => (
    <div className="rs-q-stats">
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic">{QIcn.bolt}</span>Tasks in queue</div>
            <div className="val">{counts.All || 0}</div>
            <div className="sub">across {byProject.length} project{byProject.length === 1 ? '' : 's'}</div>
        </div>
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic indigo">{QIcn.clock}</span>Due today</div>
            <div className="val">{counts['Due today'] || 0}</div>
            <div className="sub">tasks scheduled today</div>
        </div>
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic green">{QIcn.check}</span>To approve</div>
            <div className="val">{counts.approve || 0}<span className="unit">tasks</span></div>
            <div className="sub">final pass awaiting you</div>
        </div>
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic yellow">{QIcn.star}</span>Overdue</div>
            <div className="val">{counts.Overdue || 0}</div>
            <div className="sub">{counts.Overdue ? 'needs attention now' : 'clean queue'}</div>
        </div>
    </div>
);

const routeFor = (task) => {
    if (task.type === 'glossary') {
        return task.glossaryId
            ? `/dashboard/glossary/${task.glossaryId}/terms`
            : '/dashboard/glossaries';
    }
    // AI-quality "Review AI drafts" rows (server-side id prefix `qe-`) belong
    // on the AI Quality page, scoped to the project, not in the editor.
    if (task.id && String(task.id).startsWith('qe-') && task.projectId) {
        return `/dashboard/ai-quality?projectId=${task.projectId}`;
    }
    if (!task.projectId) return null;
    const params = new URLSearchParams();
    if (task.segmentN) params.set('segment', task.segmentN);
    if (task.type === 'comment') params.set('tab', 'comments');
    else if (task.type === 'review') params.set('tab', 'issues');
    const qs = params.toString();
    return `/editor/${task.projectId}${qs ? `?${qs}` : ''}`;
};

const TaskRow = ({ task }) => {
    const navigate = useNavigate();
    const go = () => {
        const to = routeFor(task);
        if (to) navigate(to);
    };
    return (
        <div className="rs-task" role="button" tabIndex={0} onClick={go}>
            <span className={`t-ic ${TYPE_CLS[task.type] || ''}`}>{TYPE_ICON[task.type] || QIcn.translate}</span>
            <div className="t-body">
                <div className="t-title">{task.title}</div>
                <div className="t-meta">
                    <b>{task.project}</b>
                    <span className="dot" />
                    <span>
                        {task.segs} segment{task.segs === 1 ? '' : 's'}
                        {task.words ? ` · ${task.words.toLocaleString()} words` : ''}
                    </span>
                </div>
            </div>
            <div className="t-chips">
                <span className="t-pair">
                    {task.pair[0]}<span className="arr">→</span>{task.pair[1]}
                </span>
            </div>
            <span className={`t-due ${task.grp === 'Overdue' ? 'warn' : task.grp === 'Due today' ? 'soon' : ''}`}>
                {task.due || '—'}
            </span>
            <button type="button" className="t-go" onClick={(e) => { e.stopPropagation(); go(); }}>
                {task.action} {QIcn.arrow}
            </button>
        </div>
    );
};

const TaskList = ({ tasks, grouped = true }) => {
    if (!grouped) {
        if (!tasks.length) {
            return (
                <div className="rs-card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>Your queue is clear</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>Nothing assigned to you. Time for a coffee.</div>
                </div>
            );
        }
        return (
            <div className="rs-tasks">
                {tasks.map((t) => <TaskRow key={t.id} task={t} />)}
            </div>
        );
    }
    const groups = GROUP_ORDER
        .map((g) => ({ name: g, items: tasks.filter((t) => t.grp === g) }))
        .filter((g) => g.items.length > 0);
    if (!groups.length) {
        return (
            <div className="rs-card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>Your queue is clear</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Nothing assigned to you. Time for a coffee.</div>
            </div>
        );
    }
    return (
        <div className="rs-tasks">
            {groups.map((g) => (
                <React.Fragment key={g.name}>
                    <div className={`rs-task-group ${g.name === 'Overdue' ? 'warn' : ''}`}>
                        <span>{g.name}</span>
                        <span className="ct">{g.items.length}</span>
                    </div>
                    {g.items.map((t) => <TaskRow key={t.id} task={t} />)}
                </React.Fragment>
            ))}
        </div>
    );
};

const SidePanel = ({ byProject, byLang, totalMinutes, onStartFocus, focusDisabled }) => {
    return (
    <div className="rs-q-side" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="rs-card rs-q-tip">
            <div className="tip-eye">Focus mode</div>
            <div className="tip-h">~{Math.max(20, totalMinutes)}m of work today</div>
            <p className="tip-p">We&apos;ll walk you through one task at a time, in urgency order, and hide the rest of the dashboard.</p>
            <button
                type="button"
                className="tip-btn"
                onClick={onStartFocus}
                disabled={focusDisabled}
                style={focusDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : null}
                title={focusDisabled ? 'Queue clear — nothing to focus on' : 'Start a focus session'}
            >
                Start focus session
            </button>
        </div>

        <div className="rs-card">
            <h3>By project</h3>
            <div className="focus-bar">
                {byProject.length === 0 && <div className="focus-row"><span className="nm">No active projects</span></div>}
                {byProject.map((row) => (
                    <div className="focus-row" key={row.nm}>
                        <span className="nm">{row.nm}</span>
                        <span className="ct">{row.ct}</span>
                    </div>
                ))}
            </div>
        </div>

        <div className="rs-card">
            <h3>By language</h3>
            <div className="focus-bar">
                {byLang.length === 0 && <div className="focus-row"><span className="nm">—</span></div>}
                {byLang.map((row) => (
                    <div className="focus-row" key={row.nm}>
                        <span className="nm">{row.nm}</span>
                        <span className="ct">{row.ct}</span>
                    </div>
                ))}
            </div>
        </div>
    </div>
    );
};

const MyQueue = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [data, setData] = useState({ items: [], counts: {}, byProject: [], byLang: [] });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('All');
    const [error, setError] = useState(null);
    const [focusOpen, setFocusOpen] = useState(() => hasActiveFocusSession());
    const [sortMode, setSortMode] = useState('priority');

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/dashboard/my-queue', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setData(r.data || { items: [], counts: {}, byProject: [], byLang: [] });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load queue.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const filtered = useMemo(() => {
        const base = filter === 'All' ? data.items : data.items.filter((i) => i.type === filter);
        if (sortMode === 'due') {
            return base.slice().sort((a, b) => {
                const at = a.rawDue ? new Date(a.rawDue).getTime() : Infinity;
                const bt = b.rawDue ? new Date(b.rawDue).getTime() : Infinity;
                return at - bt;
            });
        }
        if (sortMode === 'project') {
            return base.slice().sort((a, b) => (a.project || '').localeCompare(b.project || ''));
        }
        return base; // priority — rely on grouped render
    }, [filter, data.items, sortMode]);

    const totalMinutes = useMemo(
        () => data.items.filter((i) => i.grp === 'Due today' || i.grp === 'Overdue').reduce((s, i) => s + Math.max(5, (i.segs || 1) * 3), 0),
        [data.items],
    );

    const focusDisabled = data.items.length === 0;

    const groupCounts = useMemo(() => {
        const g = { Overdue: 0, 'Due today': 0, 'This week': 0, Later: 0 };
        data.items.forEach((i) => { g[i.grp] = (g[i.grp] || 0) + 1; });
        return g;
    }, [data.items]);

    const counts = { ...data.counts, ...groupCounts };

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading queue…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">My queue</h1>
                <span className="rs-page-title-count">{data.items.length} task{data.items.length === 1 ? '' : 's'}</span>
                <div className="rs-page-actions">
                    <button
                        type="button"
                        className="btn-q"
                        onClick={() => setSortMode((m) => (m === 'priority' ? 'due' : m === 'due' ? 'project' : 'priority'))}
                        title="Cycle sort order"
                    >
                        Sort: {sortMode === 'priority' ? 'Priority' : sortMode === 'due' ? 'Due date' : 'Project'}
                    </button>
                    <button
                        type="button"
                        className="btn-p"
                        onClick={() => setFocusOpen(true)}
                        disabled={focusDisabled}
                        style={focusDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : null}
                    >
                        Start focus session
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Tasks assigned to you across every project — sorted by what's due first.</p>

            <Stats counts={counts} byProject={data.byProject} />

            <div className="rs-views">
                {FILTERS.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        className={`rs-view-tab ${filter === f.id ? 'on' : ''}`}
                        onClick={() => setFilter(f.id)}
                    >
                        {f.label}
                        <span className="ct">{f.id === 'All' ? data.items.length : (data.counts[f.id] || 0)}</span>
                    </button>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, alignItems: 'start' }}>
                <TaskList tasks={filtered} grouped={sortMode === 'priority'} />
                <SidePanel
                    byProject={data.byProject}
                    byLang={data.byLang}
                    totalMinutes={totalMinutes}
                    onStartFocus={() => setFocusOpen(true)}
                    focusDisabled={focusDisabled}
                />
            </div>

            {focusOpen && (
                <FocusSession
                    tasks={data.items}
                    routeFor={routeFor}
                    onClose={() => setFocusOpen(false)}
                />
            )}
        </>
    );
};

export default MyQueue;
